import type {
  AdminVehicleCatalogModelDto,
  AdminVehicleCatalogPatchModelBody,
} from '@carcheck/shared';
import { useTranslation } from 'react-i18next';

import type { UseCatalogWriteResult } from './useCatalogWrite';
import { patchCatalogModel, useCatalogCache } from '../../../api/admin-vehicle-catalog';
import { publishToast } from '../../../components/feedback';

/**
 * One-click flag chips of a model row (AUT-437, «нажатие сохраняет сразу»):
 * `PATCH /models/:id` with the row's `updatedAt` verbatim and one flag.
 * The answer is a `ModelDto`, so it is spliced into the make's cached
 * detail (never replaces it); the runner then refetches the prefix. A stale
 * 409 → a toast; the refetch already loaded the fresh row.
 */
export type ModelFlag = 'popular' | 'onSale' | 'legacy' | 'hidden';

const STALE_TOAST_MS = 6000;

export const toggleBody = (
  m: AdminVehicleCatalogModelDto,
  flag: ModelFlag,
): AdminVehicleCatalogPatchModelBody => {
  if (flag === 'hidden') {
    return { updatedAt: m.updatedAt, status: m.status === 'hidden' ? 'active' : 'hidden' };
  }
  return { updatedAt: m.updatedAt, [flag]: !m[flag] };
};

export const useModelToggle = (
  makeId: string,
  write: UseCatalogWriteResult,
): ((model: AdminVehicleCatalogModelDto, flag: ModelFlag) => Promise<void>) => {
  const { t } = useTranslation();
  const cache = useCatalogCache();
  return async (model, flag) => {
    const body = toggleBody(model, flag);
    const outcome = await write.run(() => patchCatalogModel(model.id, body), {
      apply: (result) => cache.spliceModel(makeId, result),
    });
    if (!outcome.ok && outcome.error.kind === 'stale') {
      publishToast({
        severity: 'warning',
        durationMs: STALE_TOAST_MS,
        message: t('vehicleCatalog.models.staleToast'),
      });
    }
  };
};
